import React, { useState } from 'react';
import { X, Check } from 'lucide-react';
import { initials } from '../data.js';

export function Avatar({ name, color, size = '' }) {
  return (
    <span className={`avatar ${size ? `avatar--${size}` : ''}`} style={color ? { background: color } : null}>
      {initials(name || '?')}
    </span>
  );
}

export function Badge({ tone = 'neutral', children, dot = false }) {
  return (
    <span className={`badge badge--${tone}`}>
      {dot && <i className="badge-dot" />}
      {children}
    </span>
  );
}

export function StatCard({ title, value, delta, deltaTone = 'up', icon: Icon, color, subtitle }) {
  return (
    <div className="stat-card">
      <div className="stat-card-top">
        <span className="stat-card-icon" style={{ background: `${color}18`, color }}>
          <Icon size={19} />
        </span>
        {delta && <span className={`stat-delta stat-delta--${deltaTone}`}>{delta}</span>}
      </div>
      <div className="stat-card-value">{value}</div>
      <div className="stat-card-label">{title}{subtitle ? ` · ${subtitle}` : ''}</div>
    </div>
  );
}

export function Ring({ pct, color }) {
  const r = 18, c = 2 * Math.PI * r;
  return (
    <div className="health-ring">
      <svg className="ring-svg" viewBox="0 0 44 44">
        <circle className="ring-bg" cx="22" cy="22" r={r} />
        <circle
          className="ring-val"
          cx="22" cy="22" r={r}
          stroke={color}
          strokeDasharray={c}
          strokeDashoffset={c - (pct / 100) * c}
        />
      </svg>
      <span className="ring-label">{pct}</span>
    </div>
  );
}

export function EmptyState({ icon: Icon, title, desc }) {
  return (
    <div className="empty-state">
      <span className="empty-state-icon"><Icon size={22} /></span>
      <div className="empty-state-title">{title}</div>
      <div className="empty-state-desc">{desc}</div>
    </div>
  );
}

export function Modal({ title, subtitle, onClose, children, actions }) {
  return (
    <div className="modal-wrap" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-head">
          <div><h2>{title}</h2>{subtitle && <p>{subtitle}</p>}</div>
          <button className="btn-icon" onClick={onClose}><X size={17} /></button>
        </div>
        <div className="modal-body">{children}</div>
        {actions && <div className="modal-actions">{actions}</div>}
      </div>
    </div>
  );
}

// A <select> with a trailing "+ Add new…" option that flips to an inline text
// input for creating a value not yet in the list (e.g. a new owner or source).
export function CreatableSelect({ value, onChange, options, onAdd, placeholder = 'New value' }) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');

  const confirm = () => {
    const name = draft.trim();
    if (!name) { setAdding(false); return; }
    onChange(onAdd(name));
    setAdding(false);
    setDraft('');
  };
  const cancel = () => { setAdding(false); setDraft(''); };

  if (adding) {
    return (
      <div className="creatable-add-row">
        <input
          autoFocus
          placeholder={placeholder}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); confirm(); } if (e.key === 'Escape') cancel(); }}
        />
        <button type="button" className="btn-icon" onClick={confirm} title="Add"><Check size={14} /></button>
        <button type="button" className="btn-icon" onClick={cancel} title="Cancel"><X size={14} /></button>
      </div>
    );
  }

  return (
    <select value={value} onChange={(e) => (e.target.value === '__add__' ? setAdding(true) : onChange(e.target.value))}>
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      <option value="__add__">+ Add new…</option>
    </select>
  );
}

export function ProductMultiSelect({ value = [], options, onChange }) {
  const toggle = (name) => onChange(value.includes(name) ? value.filter((v) => v !== name) : [...value, name]);
  return (
    <div className="chip-select">
      {options.map((name) => (
        <label key={name} className={`chip-select-option ${value.includes(name) ? 'active' : ''}`}>
          <input type="checkbox" checked={value.includes(name)} onChange={() => toggle(name)} />
          {name}
        </label>
      ))}
    </div>
  );
}

export function ProgressBar({ pct, tone = 'info' }) {
  return (
    <div className="progress-track">
      <div className={`progress-bar progress-bar--${tone}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

export function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}
